import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Link as LinkIcon, Loader2, Podcast } from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";
import { useUser } from "util/UserContext";
import Modal from "ui/Modal";
import { Drawer, DrawerContent } from "ui/Drawer";
import InputField from "ui/InputField";
import TextEditor from "components/TextEditor";
import MediaManagerCarousel from "ui/MediaManagerCarousel";
import { useMediaManager } from "hooks/useMediaManager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/InputSelect";
import { Issue } from "util/constants";
import { createCustomIssue } from "util/issueCreationUtils";
import { standardizeUrl } from "util/textUtils";
import { uploadIssueImageFromBlob } from "util/issueCreationUtils";

type IssueTypeOption =
  | "Person"
  | "Organization"
  | "Territory"
  | "Place"
  | "Incident"
  | "CreativeWork"
  | "Artifact"
  | "Software"
  | "Regulation"
  | "Ideology"
  | "Concept"
  | "Phenomenon"
  | "Species"
  | "Material"
  | "Environment"
  | "Other";

const ISSUE_TYPE_OPTIONS: IssueTypeOption[] = [
  "Person",
  "Organization",
  "Territory",
  "Place",
  "Incident",
  "CreativeWork",
  "Artifact",
  "Software",
  "Regulation",
  "Ideology",
  "Concept",
  "Phenomenon",
  "Species",
  "Material",
  "Environment",
  "Other",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onIssueCreated?: (issue: Issue) => void;
}

/**
 * Build a Wikipedia-style cname from a user-provided title.
 * Uses the same character constraints as Wikipedia links.
 */
const buildCnameFromTitle = (title: string): string => {
  const trimmed = title.trim();
  const normalizedWhitespace = trimmed.replace(/\s+/g, "_");
  const sanitized = normalizedWhitespace.replace(/[^a-zA-Z0-9_().'%,-]/g, "_");
  const collapsed = sanitized.replace(/_+/g, "_");
  return collapsed.replace(/^_+|_+$/g, "");
};

/**
 * Strip HTML tags so we can validate rich text length.
 */
const stripHtml = (value: string): string => {
  return value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();
};

/**
 * Generate a Lucide Podcast SVG data URL for default avatars.
 */
const getPodcastSvgDataUrl = (): string => {
  const svg = renderToStaticMarkup(<Podcast size={96} />);
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

/**
 * Resolve a safe file extension for uploads.
 */
const getUploadExtension = (file: File): string => {
  const raw = file.type.split("/")[1] || "jpg";
  return raw.replace("svg+xml", "svg").replace("jpeg", "jpg");
};

const AddCustomIssueModal: React.FC<Props> = ({ open, onClose, onIssueCreated }) => {
  const { userIsMobile, currentUser, setShowAuthModal } = useUser();
  const mm = useMediaManager(1);
  const clearMedia = mm.clear;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const wasOpenRef = useRef(false);

  const [title, setTitle] = useState<string>("");
  const [issueType, setIssueType] = useState<IssueTypeOption | "">("");
  const [description, setDescription] = useState<string>("");
  const [referenceLink, setReferenceLink] = useState<string>("");
  const [showLink, setShowLink] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const defaultAvatarUrl = useMemo(() => getPodcastSvgDataUrl(), []);

  useEffect(() => {
    if (!open && wasOpenRef.current) {
      // Reset form state on close to avoid leaking prior values.
      setTitle("");
      setIssueType("");
      setDescription("");
      setReferenceLink("");
      setShowLink(false);
      setError("");
      setIsSubmitting(false);
      clearMedia();
    }
    wasOpenRef.current = open;
  }, [clearMedia, open]);

  const titleCname = useMemo(() => buildCnameFromTitle(title), [title]);
  const descriptionHasText = useMemo(() => stripHtml(description).length > 0, [description]);
  const normalizedLink = useMemo(() => {
    if (!showLink) return null;
    if (!referenceLink.trim()) return null;
    return standardizeUrl(referenceLink) || null;
  }, [referenceLink, showLink]);

  const canSubmit = useMemo(() => {
    if (!title.trim()) return false;
    if (!issueType) return false;
    if (!descriptionHasText) return false;
    if (isSubmitting || mm.isUploading) return false;
    if (showLink && referenceLink.trim() && !normalizedLink) return false;
    if (!titleCname) return false;
    return true;
  }, [title, issueType, descriptionHasText, isSubmitting, mm.isUploading, showLink, referenceLink, normalizedLink, titleCname]);

  /**
   * Handle the creation flow for a custom issue.
   * Creates the issue first, then optionally uploads the avatar image.
   */
  const handleSubmit = async (): Promise<void> => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const createdIssue = await createCustomIssue({
        title,
        cname: titleCname,
        type: issueType,
        description,
        referenceLink: normalizedLink,
        defaultThumbnail: defaultAvatarUrl,
      });

      if (mm.media.length > 0) {
        const file = mm.media[0]?.file;
        if (file) {
          const extension = getUploadExtension(file);
          await uploadIssueImageFromBlob(createdIssue.id, file, `reference_image.${extension}`);
        }
      }

      onIssueCreated?.(createdIssue);
      onClose();
    } catch (err) {
      console.error("Error creating custom issue:", err);
      setError("Could not create the issue. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerCopy = (
    <div className="custom-issue-modal-header flex flex-col gap-1">
      <h2 className="text-eoe-text-heading dark:text-eoe-dark-text-heading text-xl font-semibold">
        Add a non-Wikipedia issue
      </h2>
      <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        Only use this if Wikipedia doesn't have an article for the issue you're describing. Remember that an issue can be anything that has an influence on society.
      </p>
    </div>
  );

  const formBody = (
    <div className="custom-issue-modal-body flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
      <div className="form-control">
        <InputField
          id="custom-issue-title"
          type="text"
          name="custom-issue-title"
          labelText="Issue title"
          fieldRequired={true}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoComplete="off"
          validationState={title.trim() ? "valid" : "invalid"}
          error={!title.trim() ? "Title is required." : ""}
        />
        {!!titleCname && (
          <p className="mt-2 text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
            Cname: {titleCname}
          </p>
        )}
      </div>

      <div className="form-control">
        <label className="block text-sm font-semibold mb-2">Issue type</label>
        <Select value={issueType} onValueChange={(value) => setIssueType(value as IssueTypeOption)}>
          <SelectTrigger className="w-full px-3 py-2 rounded-xl bg-gray-200 dark:bg-eoe-dark-input">
            <SelectValue placeholder="Select a type" />
          </SelectTrigger>
          <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
            {ISSUE_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="form-control">
        <label className="block text-sm font-semibold mb-2">
          Description
          <span className="text-warning-red dark:text-dark-warning-red ml-[2px]">*</span>
        </label>
        <TextEditor
          value={description}
          onChange={setDescription}
          editorType="entity"
          placeholder="Describe the issue and why it matters..."
          fieldRequired={true}
        />
      </div>

      <div className="form-control">
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`custom-issue-media-toggle px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${mm.media.length > 0 ? "btn-primary" : "btn-secondary"}`}
          >
            <ImagePlus className="size-4" />
            <span>Avatar</span>
          </button>
          <button
            type="button"
            onClick={() => setShowLink((prev) => !prev)}
            className={`custom-issue-link-toggle px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${showLink ? "btn-primary" : "btn-secondary"}`}
          >
            <LinkIcon className="size-4" />
            <span>Link</span>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/apng,image/avif,image/svg+xml"
          className="hidden"
          onChange={(e) => mm.selectFiles(e.target.files)}
        />

        {mm.media.length > 0 ? (
          <MediaManagerCarousel
            files={mm.media.map((m) => ({ preview: m.preview, type: m.type, order: m.order }))}
            aspectClass={mm.aspectClass()}
            onMove={mm.move}
            onRemove={mm.removeAt}
            setApi={(api) => {
              mm.carouselApiRef.current = api;
            }}
            className="mt-2"
          />
        ) : (
          <div className="custom-issue-avatar-placeholder mt-2 flex items-center gap-3 rounded-xl border border-dashed border-eoe-border-main dark:border-eoe-dark-border-main p-4">
            <div className="flex items-center justify-center size-12 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon">
              <Podcast className="size-6 text-eoe-text-sub dark:text-eoe-dark-text-sub" />
            </div>
            <div className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              No avatar selected. We will use the Podcast icon by default.
            </div>
          </div>
        )}

        {mm.error && (
          <div className="mt-2 text-xs text-warning-red dark:text-warning-red-dark">
            {mm.error}
          </div>
        )}

        {showLink && (
          <InputField
            id="custom-issue-link"
            type="text"
            name="custom-issue-link"
            labelText="Reference link (optional)"
            value={referenceLink}
            onChange={(e) => setReferenceLink(e.target.value)}
            autoComplete="off"
            validationState={normalizedLink || !referenceLink.trim() ? "valid" : "invalid"}
            error={!normalizedLink && referenceLink.trim() ? "Please enter a valid URL." : ""}
          />
        )}
      </div>

      {error && (
        <div className="text-warning-red dark:text-warning-red-dark text-sm">
          {error}
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="flex flex-grow flex-col py-2 justify-end place-items-end mb-0 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row">
      <button type="button" onClick={onClose} className="btn-secondary w-full sm:w-fit py-2 px-4">
        Cancel
      </button>
      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className={`w-full sm:w-fit py-2 px-4 ${!canSubmit ? "btn-primary-disabled opacity-50 cursor-not-allowed" : "btn-primary"}`}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
          </span>
        ) : (
          "Create issue"
        )}
      </button>
    </div>
  );

  if (!open) return null;

  if (userIsMobile) {
    return (
      <Drawer open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
        <DrawerContent title="Add a non-Wikipedia issue" footer={footer} stickyFooter>
          <div className="px-4 pb-4 pt-2">
            {headerCopy}
            {formBody}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={open} onClose={onClose} title={headerCopy} footer={footer} stickyFooter>
      <div className="pt-2">{formBody}</div>
    </Modal>
  );
};

export default AddCustomIssueModal;
